import AxiosInstance from '../config/AxiosInstance';
import AxiosInstanceFormData from '../config/AxiosInstanceFormData';

/**
 * Wire shapes mirror dev-inventory's response DTOs (FIN-1125).
 * See docs/FIN-1125-bulk-product-import.md in the API repo.
 */

export type ProductImportBatchStatus =
    | 'PREVIEWED'
    | 'CONFIRMING'
    | 'CONFIRMED'
    | 'FAILED'
    | 'EXPIRED';

export type ProductImportRowStatus =
    | 'READY_CREATE'
    | 'SKIP_EXISTING'
    | 'NEEDS_ATTENTION';

export interface ProductImportRowMessage {
    field: string;
    level: 'info' | 'warning' | 'error';
    code: string;
    message: string;
}

export interface ProductImportBatch {
    id: string;
    companyId: string;
    createdBy: string;
    status: ProductImportBatchStatus;
    filename: string | null;
    totalRows: number;
    readyCount: number;
    skipCount: number;
    needsAttentionCount: number;
    templateVersion: string;
    parserVersion: string;
    errorDetail: string | null;
    confirmedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ProductImportRow {
    id: string;
    rowNumber: number;
    rowStatus: ProductImportRowStatus;
    sku: string | null;
    rawPayload: Record<string, unknown>;
    resolvedPayload: Record<string, unknown> | null;
    messages: ProductImportRowMessage[];
    createdProductId: string | null;
}

export interface PreviewResponse {
    batch: ProductImportBatch;
    rows: ProductImportRow[];
    page: number;
    limit: number;
    total: number;
}

export interface Paginated<T> {
    data: T[];
    page: number;
    limit: number;
    total: number;
}

const BASE = '/api/inventory/product-import';

/** Upload an XLSX and produce a preview batch. */
export const previewProductImport = async (file: File): Promise<PreviewResponse> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await AxiosInstanceFormData.post<PreviewResponse>(`${BASE}/preview`, form);
    return data;
};

/** Paginated list of past import batches for the current company. */
export const listProductImportBatches = async (
    page = 1,
    limit = 20,
): Promise<Paginated<ProductImportBatch>> => {
    const { data } = await AxiosInstance.get<Paginated<ProductImportBatch>>(`${BASE}/batches`, {
        params: { page, limit },
    });
    return data;
};

/** Fetch one batch summary. */
export const getProductImportBatch = async (id: string): Promise<ProductImportBatch> => {
    const { data } = await AxiosInstance.get<ProductImportBatch>(`${BASE}/batches/${id}`);
    return data;
};

/** Fetch rows for a batch, optionally filtered by status. */
export const listProductImportRows = async (
    batchId: string,
    page = 1,
    limit = 100,
    rowStatus?: ProductImportRowStatus,
): Promise<Paginated<ProductImportRow>> => {
    const params: Record<string, string | number> = { page, limit };
    if (rowStatus) params.rowStatus = rowStatus;
    const { data } = await AxiosInstance.get<Paginated<ProductImportRow>>(
        `${BASE}/batches/${batchId}/rows`,
        { params },
    );
    return data;
};

/** Confirm a previewed batch — commits the catalog rows. */
export const confirmProductImportBatch = async (id: string): Promise<ProductImportBatch> => {
    const { data } = await AxiosInstance.post<ProductImportBatch>(`${BASE}/batches/${id}/confirm`);
    return data;
};
